import React, { useEffect, useState } from 'react';
import { Building2, KeyRound, Crown, User, ArrowRight, Lock, FlaskConical } from 'lucide-react';
import InvestorPortal from '../../components/InvestorPortal';
import TenantPortal from '../tenant/TenantPortal';
import CEOPortal from '../ceo/CEOPortal';
import GuestPortal from '../guest/GuestPortal';
import { useLanguage, LanguageSelector } from '../../components/LanguageContext';
import { theme } from '../shared/theme';
import { DemoView, isValidDemoCode, isDemoUnlocked, markDemoUnlocked } from './demoMode';
import { installDemoNetworkBlock } from './demoNetwork';
import '../shared/portals.css';

// ============================================================
// DemoShell — the code-gated product showcase.
//
// Management needs to show anyone what the product looks like from every
// side. A real login shows exactly one side, and only to someone who owns a
// unit — so this runs all four portals on bundled sample fixtures:
//
//   Property Owner → the real InvestorPortal in demoMode
//   Tenant         → the real TenantPortal on its MOCK_VM
//   CEO            → the real CEOPortal on CEO_MOCK
//   Airbnb Guest   → the real GuestPortal (sample stays by design)
//
// These are the actual screens, not mockups of them, so the showcase cannot
// drift away from the product as it changes.
//
// SAFETY: the whole session runs with API traffic blocked at window.fetch
// (see demoNetwork.ts). That containment is installed here, at the shell,
// so it covers every view and every child component — including any fetch
// added later by someone who never reads this file. The access code is a
// speed bump only; see the note in demoMode.ts.
// ============================================================

interface Props {
  onChangeRole?: () => void;
}

// ---------------- Code gate ----------------
const DemoGate: React.FC<{ onUnlock: () => void; onChangeRole?: () => void }> = ({ onUnlock, onChangeRole }) => {
  const { t } = useLanguage();
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidDemoCode(code)) {
      markDemoUnlocked();
      onUnlock();
    } else {
      setError(true);
    }
  };

  return (
    <div className="pt-screen">
      <div style={{ padding: '18px 14px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <LanguageSelector compact />
        </div>

        <header style={{ textAlign: 'center', marginBottom: 22 }}>
          <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="pl-logo" />
          <div className="pl-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>
          <div className="pl-eyebrow">{t('dmGateEyebrow')}</div>
          <h1 className="pl-title">{t('dmGateTitle')}</h1>
          <p className="pl-sub">{t('dmGateSub')}</p>
        </header>

        <form onSubmit={submit} style={{ maxWidth: 340, margin: '0 auto' }}>
          <label
            htmlFor="demo-code"
            style={{
              display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: theme.slate, marginBottom: 6,
            }}
          >
            {t('dmGateLabel')}
          </label>
          <input
            id="demo-code"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(false); }}
            autoFocus
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            style={{
              width: '100%', padding: '12px 14px', fontSize: 16, letterSpacing: '0.08em',
              borderRadius: 10, border: `1px solid ${error ? theme.danger : theme.line}`,
              background: '#fff', color: theme.ink, textAlign: 'center', fontWeight: 600,
            }}
          />
          {error && (
            <p style={{ fontSize: 12.5, color: theme.danger, margin: '8px 0 0', textAlign: 'center' }}>
              {t('dmGateWrong')}
            </p>
          )}
          <button type="submit" className="pl-btn" style={{ marginTop: 16 }} disabled={!code.trim()}>
            {t('dmGateButton')}
          </button>
        </form>

        <footer
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            marginTop: 22, fontSize: 11.5, color: theme.slateLight,
          }}
        >
          <Lock size={11} strokeWidth={2} />
          <span>{t('dmGateFoot')}</span>
        </footer>

        {onChangeRole && (
          <button onClick={onChangeRole} className="pl-changerole">← {t('pcChangeRole')}</button>
        )}
      </div>
    </div>
  );
};

// ---------------- View picker ----------------
const VIEWS: { id: DemoView; title: string; sub: string; icon: React.ComponentType<any>; color: string }[] = [
  { id: 'owner', title: 'dmHubOwner', sub: 'dmHubOwnerSub', icon: Building2, color: '#B99A4C' },
  { id: 'tenant', title: 'dmHubTenant', sub: 'dmHubTenantSub', icon: KeyRound, color: '#2563eb' },
  { id: 'ceo', title: 'dmHubCeo', sub: 'dmHubCeoSub', icon: Crown, color: '#B8933F' },
  { id: 'guest', title: 'dmHubGuest', sub: 'dmHubGuestSub', icon: User, color: '#3F3B34' },
];

const DemoHub: React.FC<{ onPick: (v: DemoView) => void; onChangeRole?: () => void }> = ({ onPick, onChangeRole }) => {
  const { t } = useLanguage();
  return (
    <div className="pt-screen">
      <div style={{ padding: '18px 14px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <LanguageSelector compact />
        </div>

        <header style={{ textAlign: 'center', marginBottom: 18 }}>
          <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="pl-logo" />
          <div className="pl-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>
          <div className="pl-eyebrow">{t('dmHubEyebrow')}</div>
          <h1 className="pl-title">{t('dmHubTitle')}</h1>
          <p className="pl-sub">{t('dmHubSub')}</p>
        </header>

        <div
          style={{
            display: 'flex', gap: 10, alignItems: 'flex-start',
            background: '#fffbeb', border: '1px solid #fcd34d',
            borderRadius: 10, padding: '10px 12px', marginBottom: 16,
          }}
        >
          <FlaskConical size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 12, color: '#a16207', margin: 0, lineHeight: 1.45 }}>
            {t('dmHubNotice')}
          </p>
        </div>

        <div className="pt-stagger" style={{ display: 'grid', gap: 10 }}>
          {VIEWS.map(({ id, title, sub, icon: Icon, color }) => (
            <button
              key={id}
              onClick={() => onPick(id)}
              style={{
                width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: 13, background: '#fff',
                border: `1px solid ${theme.line}`, borderRadius: 14, padding: 16,
                boxShadow: theme.shadow,
              }}
            >
              <span
                style={{
                  width: 40, height: 40, borderRadius: 12, flexShrink: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', background: '#f9fafb',
                }}
              >
                <Icon size={19} color={color} strokeWidth={1.9} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: theme.ink }}>{t(title)}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: theme.slate, marginTop: 2, lineHeight: 1.4 }}>
                  {t(sub)}
                </span>
              </span>
              <ArrowRight size={16} color={theme.slateLight} strokeWidth={1.8} style={{ flexShrink: 0 }} />
            </button>
          ))}
        </div>

        {onChangeRole && (
          <button onClick={onChangeRole} className="pl-changerole">← {t('pcChangeRole')}</button>
        )}
      </div>
    </div>
  );
};

// ---------------- Shell ----------------
const DemoShell: React.FC<Props> = ({ onChangeRole }) => {
  const [unlocked, setUnlocked] = useState(isDemoUnlocked());
  const [view, setView] = useState<DemoView | null>(null);

  // Containment for the whole demo session, installed before any view mounts
  // and removed when the shell unmounts (i.e. on "change role").
  useEffect(() => installDemoNetworkBlock(), []);

  if (!unlocked) {
    return <DemoGate onUnlock={() => setUnlocked(true)} onChangeRole={onChangeRole} />;
  }

  if (!view) {
    return <DemoHub onPick={setView} onChangeRole={onChangeRole} />;
  }

  // Inside a view, the portal's own "change role" control returns to the
  // picker rather than leaving the showcase — that is the useful back step
  // when someone is walking an audience through all four.
  const backToHub = () => setView(null);

  switch (view) {
    case 'tenant':
      return <TenantPortal demoMode onChangeRole={backToHub} />;
    case 'ceo':
      return <CEOPortal demoMode onChangeRole={backToHub} />;
    case 'guest':
      return <GuestPortal onChangeRole={backToHub} />;
    case 'owner':
    default:
      return <InvestorPortal demoMode onChangeRole={backToHub} />;
  }
};

export default DemoShell;
